import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUsers, FiCalendar, FiBookOpen, FiBriefcase, FiUser, FiArrowRight, FiFilter, FiHome } from 'react-icons/fi';
import { MobileAlumniNav, MobileAlumniBottomNav } from './components/AlumniMobileComponents';
import { PATHS } from './utils/platformPaths';

const AlumniGroups = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'graduation_year', 'major', 'my_groups'

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [groups, filterType]);

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(PATHS.login);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/groups`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate(PATHS.login);
          return;
        }
        throw new Error('Failed to fetch alumni groups');
      }

      const data = await response.json();
      setGroups(data.groups || []);
      setError(null);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setError('Failed to load alumni groups');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...groups];

    if (filterType === 'graduation_year') {
      filtered = filtered.filter(group => group.type === 'graduation_year');
    } else if (filterType === 'major') {
      filtered = filtered.filter(group => group.type === 'major');
    } else if (filterType === 'my_groups') {
      filtered = filtered.filter(group => group.is_member);
    }

    setFilteredGroups(filtered);
  };

  const fetchGroupMembers = async (groupId, groupName) => {
    setLoadingMembers(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate(PATHS.login);
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/alumni/groups/${groupId}/members?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate(PATHS.login);
          return;
        }
        throw new Error('Failed to fetch group members');
      }

      const data = await response.json();
      setGroupMembers(data.members || []);
      setSelectedGroup({ id: groupId, name: groupName });
    } catch (error) {
      console.error('Error fetching group members:', error);
      setGroupMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const viewProfile = (alumniId) => {
    window.open(`/alumni/profile/${alumniId}`, '_blank');
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <FiUsers className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Unable to load groups</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <button
            onClick={fetchGroups}
            className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Navigation */}
      <MobileAlumniNav 
        title="Groups" 
        onMenuClick={() => navigate(PATHS.dashboard)}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20 md:pb-8">
      {/* Header - Desktop */}
      <div className="hidden md:block mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Alumni Groups</h1>
            <p className="text-gray-600">Connect with fellow alumni from your graduation year and field of study.</p>
          </div>
          <button
            onClick={() => navigate(PATHS.dashboard)}
            className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-md text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
          >
            <FiHome className="h-4 w-4 mr-2" />
            Dashboard
          </button>
        </div>
      </div>

      {/* Header - Mobile */}
      <div className="md:hidden mb-6">
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Alumni Groups</h1>
          <p className="text-gray-600">Connect with fellow alumni from your graduation year and field of study.</p>
        </div>
        <div className="flex justify-center">
          <button
            onClick={() => navigate(PATHS.dashboard)}
            className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-md text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
          >
            <FiHome className="h-4 w-4 mr-2" />
            Dashboard
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex space-x-2">
          {[
            { key: 'all', label: 'All Groups' },
            { key: 'my_groups', label: 'My Groups' },
            { key: 'graduation_year', label: 'By Year' },
            { key: 'major', label: 'By Major' }
          ].map((filter) => (
            <button
              key={filter.key}
              onClick={() => setFilterType(filter.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filterType === filter.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="text-sm text-gray-500 mt-2">
          {filteredGroups.length} groups found
        </div>
      </div>

      {/* Groups Grid */}
      {filteredGroups.length === 0 ? (
        <div className="text-center py-12">
          <FiUsers className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No groups found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {filterType === 'my_groups' 
              ? "You're not a member of any groups yet. Browse all groups to find communities to join."
              : "Try adjusting your filter or check back later as more alumni join."
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGroups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              onViewMembers={fetchGroupMembers}
            />
          ))}
        </div>
      )}

      {/* Group Members Modal */}
      {selectedGroup && (
        <GroupMembersModal
          group={selectedGroup}
          members={groupMembers}
          loading={loadingMembers}
          onClose={() => {
            setSelectedGroup(null);
            setGroupMembers([]);
          }}
          onViewProfile={viewProfile}
        />
      )}
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileAlumniBottomNav 
        activeSection="groups"
        onNavigate={(path) => navigate(path)}
      />
    </>
  );
};

const GroupCard = ({ group, onViewMembers }) => {
  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-200">
      <div className="p-6">
        {/* Group Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-3 rounded-full ${group.type === 'graduation_year' ? 'bg-blue-100' : 'bg-green-100'}`}>
              {group.type === 'graduation_year' ? (
                <FiCalendar className={`h-6 w-6 ${group.type === 'graduation_year' ? 'text-blue-600' : 'text-green-600'}`} />
              ) : (
                <FiBookOpen className="h-6 w-6 text-green-600" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 text-center">{group.title}</h3>
              <p className="text-sm text-gray-600 text-center">{group.description}</p>
            </div>
          </div>
          {group.is_member && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Member
            </span>
          )}
        </div>

        {/* Group Stats */}
        <div className="flex items-center justify-center text-sm text-gray-600 mb-4">
          <FiUsers className="h-4 w-4 mr-1" />
          <span>{group.member_count} alumni</span>
        </div>

        {/* Top Employers */}
        {group.top_employers && group.top_employers.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2 text-center">Top Employers</h4>
            <div className="flex flex-wrap justify-center gap-1">
              {group.top_employers.slice(0, 3).map((employer, index) => (
                <span
                  key={index}
                  className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded"
                >
                  {employer}
                </span>
              ))}
              {group.top_employers.length > 3 && (
                <span className="text-xs text-gray-500 text-center">+{group.top_employers.length - 3} more</span>
              )}
            </div>
          </div>
        )}

        {/* Additional Info */}
        {group.type === 'graduation_year' && group.majors_represented && group.majors_represented.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2 text-center">Popular Majors</h4>
            <div className="flex flex-wrap justify-center gap-1">
              {group.majors_represented.slice(0, 2).map((major, index) => (
                <span
                  key={index}
                  className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded"
                >
                  {major}
                </span>
              ))}
              {group.majors_represented.length > 2 && (
                <span className="text-xs text-gray-500 text-center">+{group.majors_represented.length - 2} more</span>
              )}
            </div>
          </div>
        )}

        {group.type === 'major' && group.graduation_years && group.graduation_years.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2 text-center">Graduation Years</h4>
            <div className="flex flex-wrap justify-center gap-1">
              {group.graduation_years.slice(0, 4).map((year, index) => (
                <span
                  key={index}
                  className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded"
                >
                  {year}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* View Members Button */}
        <button
          onClick={() => onViewMembers(group.id, group.title)}
          className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
        >
          View Members
          <FiArrowRight className="ml-2 h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const GroupMembersModal = ({ group, members, loading, onClose, onViewProfile }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{group.name}</h3>
              <p className="text-sm text-gray-600">{members.length} members shown</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Members List */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-8">
              <FiUser className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No members found</h3>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {members.map((member) => (
                <MemberCard key={member.id} member={member} onViewProfile={onViewProfile} />
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const MemberCard = ({ member, onViewProfile }) => {
  return (
    <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
      <div className="flex items-start space-x-3">
        {/* Profile Photo */}
        <div className="flex-shrink-0">
          {member.headshot_url ? (
            <img
              className="h-12 w-12 rounded-full object-cover"
              src={member.headshot_url}
              alt={`${member.first_name} ${member.last_name}`}
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
              <FiUser className="h-6 w-6 text-gray-600" />
            </div>
          )}
        </div>

        {/* Member Info */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 truncate">
            {member.first_name} {member.last_name}
          </h4>
          
          {/* Current Position & Company */}
          {member.show_current_position && member.current_position && (
            <p className="text-xs text-gray-600 truncate">{member.current_position}</p>
          )}
          {member.show_current_employer && member.current_employer && (
            <div className="flex items-center mt-1">
              {member.current_company_logo && (
                <img
                  src={member.current_company_logo}
                  alt={member.current_employer}
                  className="h-3 w-3 rounded mr-1 bg-white"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <p className="text-xs text-blue-600 font-medium truncate">{member.current_employer}</p>
            </div>
          )}

          {/* Graduation Year */}
          {member.show_graduation_year && member.graduation_year && (
            <div className="flex items-center mt-1 text-xs text-gray-500">
              <FiCalendar className="h-3 w-3 mr-1" />
              <span>Class of {member.graduation_year}</span>
            </div>
          )}

          {/* View Profile Button */}
          <button
            onClick={() => onViewProfile(member.id)}
            className="w-full mt-3 text-center text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 py-1.5 px-3 rounded-md transition-colors"
          >
            View Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlumniGroups;